import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ListTodo, AlertTriangle, CalendarDays, Building2, TrendingUp,
  Handshake, Wallet, ArrowUpRight, Activity as ActivityIcon, Mail,
} from "lucide-react";

const TONE_MAP = {
  slate: "bg-slate-100 text-slate-600",
  blue: "bg-blue-50 text-blue-600",
  emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  rose: "bg-rose-50 text-rose-600",
};
const KpiCard = ({ label, value, icon: Icon, tone = "slate", testId }) => (
  <Card
    data-testid={testId}
    className="border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-transform duration-200"
  >
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
          <div className="mt-2 text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {value}
          </div>
        </div>
        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${TONE_MAP[tone] || TONE_MAP.slate}`}>
          <Icon className="h-4 w-4" strokeWidth={2} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, a] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/activities", { params: { limit: 15 } }),
      ]);
      setData(s.data);
      setActivities(a.data);
    })();
  }, []);

  if (!data) return <div className="text-slate-500">Loading dashboard...</div>;
  const k = data.kpi;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Overview</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Here's what's happening across GameLink today.</p>
        </div>
        {["super_admin", "admin"].includes(user?.role) && (
          <Button
            data-testid="send-digest-button"
            variant="outline"
            className="border-slate-200"
            onClick={async () => {
              try {
                const { data } = await api.post("/digest/send-now");
                if (data.email_id) toast.success(`Digest sent to ${data.recipient}`);
                else toast.error("Email service unavailable — check config");
              } catch { toast.error("Failed to send digest"); }
            }}
          >
            <Mail className="h-4 w-4 mr-2" /> Send Daily Digest
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KpiCard label="Open Tasks" value={k.open_tasks} icon={ListTodo} testId="kpi-open-tasks" />
        <KpiCard label="Overdue" value={k.overdue_tasks} icon={AlertTriangle} tone="rose" testId="kpi-overdue" />
        <KpiCard label="Due Today" value={k.due_today} icon={CalendarDays} tone="amber" testId="kpi-due-today" />
        <KpiCard label="Active Venues" value={k.active_venues} icon={Building2} tone="emerald" testId="kpi-venues" />
        <KpiCard label="Upcoming Events" value={k.upcoming_events} icon={CalendarDays} tone="blue" testId="kpi-events" />
        <KpiCard label="Active Leads" value={k.active_leads} icon={TrendingUp} tone="blue" testId="kpi-leads" />
        <KpiCard label="Sponsor Pipeline" value={fmtINR(k.sponsor_pipeline)} icon={Handshake} tone="amber" testId="kpi-sponsor-pipeline" />
        <KpiCard label="Receivables" value={fmtINR(k.receivables_total)} icon={Wallet} tone="rose" testId="kpi-receivables" />
        <KpiCard label="Revenue (MTD)" value={fmtINR(k.revenue_month)} icon={ArrowUpRight} tone="emerald" testId="kpi-revenue" />
        <KpiCard label="Expenses (MTD)" value={fmtINR(k.expenses_month)} icon={Wallet} tone="slate" testId="kpi-expenses" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Needs Attention</h2>
              <AlertTriangle className="h-4 w-4 text-rose-600" />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Overdue tasks</div>
                {data.needs_attention.overdue_tasks.length === 0 ? (
                  <div className="text-sm text-slate-500">No overdue tasks. 🎯</div>
                ) : (
                  <ul className="space-y-2">
                    {data.needs_attention.overdue_tasks.map((t) => (
                      <li key={t.id} className="flex items-center justify-between border-l-4 border-rose-500 pl-3 py-1.5 bg-rose-50/50 rounded-r-md">
                        <div>
                          <div className="text-sm font-medium text-slate-900">{t.title}</div>
                          <div className="text-xs text-slate-500">{t.assignee_name} · due {fmtDate(t.due_date)}</div>
                        </div>
                        <StatusBadge status={t.priority} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Stale leads (7+ days)</div>
                {data.needs_attention.stale_leads.length === 0 ? (
                  <div className="text-sm text-slate-500">No stale leads. All followed up.</div>
                ) : (
                  <ul className="space-y-2">
                    {data.needs_attention.stale_leads.map((l) => (
                      <li key={l.id} className="flex items-center justify-between border-l-4 border-amber-500 pl-3 py-1.5 bg-amber-50/50 rounded-r-md">
                        <div>
                          <div className="text-sm font-medium text-slate-900">{l.company}</div>
                          <div className="text-xs text-slate-500">{l.assigned_to_name}</div>
                        </div>
                        <StatusBadge status={l.status} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Unpaid invoices</div>
                {data.needs_attention.unpaid_invoices.length === 0 ? (
                  <div className="text-sm text-slate-500">No unpaid invoices found.</div>
                ) : (
                  <ul className="space-y-2">
                    {data.needs_attention.unpaid_invoices.map((r) => (
                      <li key={r.id} className="flex items-center justify-between border-l-4 border-rose-500 pl-3 py-1.5 bg-rose-50/50 rounded-r-md">
                        <div>
                          <div className="text-sm font-medium text-slate-900">{r.client}</div>
                          <div className="text-xs text-slate-500">{r.invoice_number} · {fmtINR(r.balance)}</div>
                        </div>
                        <StatusBadge status={r.status} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Blocked tasks</div>
                {data.needs_attention.blocked_tasks.length === 0 ? (
                  <div className="text-sm text-slate-500">No blocked tasks.</div>
                ) : (
                  <ul className="space-y-2">
                    {data.needs_attention.blocked_tasks.map((t) => (
                      <li key={t.id} className="flex items-center justify-between border-l-4 border-slate-400 pl-3 py-1.5 bg-slate-50 rounded-r-md">
                        <div>
                          <div className="text-sm font-medium text-slate-900">{t.title}</div>
                          <div className="text-xs text-slate-500">{t.assignee_name}</div>
                        </div>
                        <StatusBadge status={t.status} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Recent Activity</h2>
              <ActivityIcon className="h-4 w-4 text-blue-600" />
            </div>
            <ul className="space-y-3 max-h-[500px] overflow-y-auto">
              {activities.length === 0 && <li className="text-sm text-slate-500">No recent activity yet.</li>}
              {activities.map((a) => (
                <li key={a.id} className="text-sm">
                  <div className="text-slate-900">
                    <span className="font-semibold">{a.actor_name}</span> {a.action}
                    {a.entity_name && <span className="text-slate-500"> · {a.entity_name}</span>}
                  </div>
                  <div className="text-xs text-slate-400">{fmtDate(a.created_at)}</div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Upcoming Events</h2>
          {data.upcoming.events.length === 0 ? (
            <div className="text-sm text-slate-500">No events in the next 30 days.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.upcoming.events.map((e) => (
                <Link key={e.id} to={`/events/${e.id}`} className="block border border-slate-200 rounded-md p-4 hover:shadow-sm hover:-translate-y-0.5 transition-transform bg-white">
                  <div className="flex items-start justify-between">
                    <div className="font-semibold text-slate-900">{e.name}</div>
                    <StatusBadge status={e.status} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{e.venue_name} · {fmtDate(e.date)}</div>
                  <div className="text-xs text-slate-500 mt-1">{e.type}</div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
